import React, { FC, useState } from "react";
import { Alert, Table, Spin, Typography, Modal } from "antd";
import {
  Price,
  PriceRange,
  formatPrice,
  joinPricing,
  isPriceRange,
  MatterInvoice,
  Gpm as GpmDataType,
} from "../../data-types";
import styles from "./historic-info.module.css";
import { TaxLabel } from "~/gql/graphql";
import { Colors } from "../colors";
import { GpmSlider, indicatorPropsFromGpm } from "../gpm-slider";
import { useGpmSettingsCheck } from "../../../hooks";
import { calculateGpm, calculateMixedGpm } from "../matter-editor/sum-legal-fee-costs";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const { Text } = Typography;

const {
  label: labelClassName,
  error: errorClassName,
  success: successClassName,
  normal: normalClassName,
  table: tableClassName,
  container: containerClassName,
  summary: summaryClassName,
  "metrics-grid": metricsGridClassName,
  "metrics-row": metricsRowClassName,
  "metric-card": metricCardClassName,
  "metric-card--clickable": metricCardClickableClassName,
  "modal-table": modalTableClassName,
  "gpm-row-spacer": gpmRowSpacerClassName,
  value: valueClassName,
  alert: alertClassName,
  "secondary-text": secondaryTextClassName,
  "slider-container": sliderContainerClassName,
} = styles as any;

const statusColors = {
  red: Colors.Error,
  amber: Colors.Warning,
  green: Colors.Success,
};

export type HistoricInfoProps = {
  loading?: boolean;
  data?: {
    agreedPrice?: Price | PriceRange | null;
    plannedFee?: Price | PriceRange | null;
    actualFee: number;
    actualGpm: number;
    gpmDangerThreshold: number | undefined;
    gpmWarningThreshold: number | null | undefined;
    accruedTimes: {
      feeEarnerId: number;
      feeEarnerName: string;
      feeEarnerLevelDescription: string;
      actualDuration: number;
      actualFee: number;
      actualCost: number;
      durationAsPercentageOfTotalMatterDuration: number;
    }[];
    currencySymbol: string;
    taxLabel?: TaxLabel;
    invoices?: MatterInvoice[];
    amountBilled: number;
  };
  matterId?: number;
  showAlert?: boolean;
};

enum Tones {
  "Error" = "error",
  "Normal" = "normal",
  "Success" = "success",
}
const doColumns = (currencySymbol: string, feeEarnerLevelLabel: string) => {
  return [
    {
      title: `Name & ${feeEarnerLevelLabel}`,
      key: "feeEarnerName",
      render: (
        _text: string,
        {
          feeEarnerName,
          feeEarnerLevelDescription,
        }: { feeEarnerName: string; feeEarnerLevelDescription: string }
      ) => `${feeEarnerName} (${feeEarnerLevelDescription})`,
    },
    {
      title: "Hours",
      key: "actualDuration",
      render: (_text: string, { actualDuration }: { actualDuration: number }) =>
        (actualDuration / 60).toFixed(1),
    },
    {
      title: "%",
      key: "durationAsPercentageOfTotalMatterDuration",
      render: (
        _text: string,
        {
          durationAsPercentageOfTotalMatterDuration,
        }: { durationAsPercentageOfTotalMatterDuration: number }
      ) => Math.round(durationAsPercentageOfTotalMatterDuration),
    },
    {
      title: currencySymbol,
      key: "actualFee",
      render: (_text: string, { actualFee }: { actualFee: number }) =>
        formatPrice(actualFee, currencySymbol),
    },
  ];
};

const getGpmValue = (gpm: GpmDataType | number | null): number => {
  if (!gpm) return 0;
  if (typeof gpm === "number") return gpm;
  if ("fixed" in gpm) return gpm.fixed;
  return (gpm.lower + gpm.upper) / 2; // For range GPM, use the average
};

export const HistoricInfo: FC<HistoricInfoProps> = ({ loading, data, showAlert = true }) => {
  const taxonomy = useTaxonomyLabel();

  if (loading || !data) return <Spin />;
  const {
    actualGpm,
    accruedTimes,
    currencySymbol,
    taxLabel,
    gpmDangerThreshold,
    gpmWarningThreshold,
    plannedFee,
    invoices,
    amountBilled,
  } = data;
  const { totalMinutes, totalFee, totalCost } = accruedTimes.reduce(
    (acc, { actualDuration, actualFee, actualCost }) => ({
      totalMinutes: acc.totalMinutes + actualDuration,
      totalFee: acc.totalFee + actualFee,
      totalCost: acc.totalCost + actualCost,
    }),
    { totalMinutes: 0, totalFee: 0, totalCost: 0 }
  );

  const totalHours = totalMinutes / 60;

  const markup = joinPricing([plannedFee, -totalFee]);

  const { showGpm, showGpmSlider } = useGpmSettingsCheck();

  const belowGpmThreshold = gpmDangerThreshold ? actualGpm < gpmDangerThreshold : false;
  const roundedActualGpm = Math.round(actualGpm);

  const gpmAfterWriteOff = calculateMixedGpm({
    cost: [totalCost],
    price: [plannedFee],
  });

  const gpmRecovered = calculateGpm(totalCost, amountBilled);

  let gpmStatus;
  if (gpmDangerThreshold && gpmWarningThreshold) {
    const indicatorProps = indicatorPropsFromGpm(
      actualGpm,
      gpmDangerThreshold,
      gpmWarningThreshold
    );
    gpmStatus = indicatorProps.status;
  } else {
    gpmStatus = null;
  }
  const gpmText = showGpm ? (
    `${roundedActualGpm}%`
  ) : gpmStatus ? (
    <span>
      in the <b style={{ color: statusColors[gpmStatus] }}>{gpmStatus}</b>
    </span>
  ) : null;
  const recommendedText = showGpm ? `${gpmDangerThreshold}%` : "target";

  return (
    <div className={containerClassName}>
      {showAlert && belowGpmThreshold && accruedTimes.length > 0 && (showGpm || showGpmSlider) ? (
        <Alert
          className={alertClassName}
          message="Warning"
          description={
            <div>
              The Gross Profit Margin on this matter was {gpmText} which is below the firm&apos;s
              recommended {recommendedText}. If you propose to base a new proposal on this matter,
              you should consider:
              <ul className={tableClassName}>
                <li>Increasing the price</li>
                <li>Reducing the scope</li>
                <li>Delegate more work to lower cost {taxonomy.FeeEarner}s</li>
              </ul>
            </div>
          }
          type="warning"
          closable
        />
      ) : null}

      <div className={metricsGridClassName}>
        {/* Fees Row */}
        <div className={metricsRowClassName}>
          {/* Price quoted - only if it exists */}
          {plannedFee && (
            <MetricCard
              label="Price quoted"
              value={formatPrice(plannedFee, currencySymbol)}
              secondary={`plus ${taxLabel ? taxLabel : "tax"} and disbursements`}
            />
          )}

          {/* Actual time accrued - always show (either card or alert) */}
          {accruedTimes.length > 0 ? (
            <MetricCard
              label="Actual time accrued"
              value={formatPrice(totalFee, currencySymbol)}
              secondary={`plus ${taxLabel ? taxLabel : "tax"} and disbursements`}
              expandable={
                <Table
                  columns={doColumns(currencySymbol, taxonomy.FeeEarnerLevel)}
                  dataSource={accruedTimes}
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2}>
                        <Text className={summaryClassName}>{totalHours.toFixed(1)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Text className={summaryClassName}>100</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <Text className={summaryClassName}>
                          {formatPrice(totalFee, currencySymbol)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              }
            />
          ) : (
            <Alert
              message="No Time Entries"
              description="There are no time entries recorded for this historical matter. This could indicate that the matter is still ongoing, or that time was not recorded correctly."
              type="info"
            />
          )}

          {/* Markup/write-off - only if price quoted exists AND we have time entries */}
          {plannedFee &&
            accruedTimes.length > 0 &&
            (isPriceRange(markup) ? (
              <MetricCard
                label="Markup/write-off"
                value={`${formatPrice(markup.lower || 0, currencySymbol)} to ${formatPrice(
                  markup.upper || 0,
                  currencySymbol
                )}`}
                tone={{
                  lower: (markup.lower || 0) >= 0 ? Tones.Success : Tones.Error,
                  upper: (markup.upper || 0) >= 0 ? Tones.Success : Tones.Error,
                }}
              />
            ) : (
              <MetricCard
                label="Markup/write-off"
                value={formatPrice(markup, currencySymbol)}
                tone={markup >= 0 ? Tones.Success : Tones.Error}
              />
            ))}

          {/* Amount billed - only if invoices exist */}
          {data.invoices && data.invoices.length > 0 && (
            <MetricCard
              label="Amount billed"
              value={formatPrice(amountBilled, currencySymbol)}
              secondary={`plus ${taxLabel ? taxLabel : "tax"} and disbursements`}
              expandable={
                <Table
                  columns={[
                    {
                      title: "Description",
                      dataIndex: "description",
                      key: "description",
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (value: string) => value.toUpperCase(),
                    },
                    {
                      title: "Total Net",
                      dataIndex: "totalFeeNet",
                      key: "totalFeeNet",
                      render: (value: number) => formatPrice(value, currencySymbol),
                    },
                    {
                      title: "Date",
                      dataIndex: "date",
                      key: "date",
                      render: (value: string) => new Date(value).toLocaleDateString(),
                    },
                  ]}
                  dataSource={invoices}
                  pagination={false}
                />
              }
            />
          )}
        </div>

        {/* GPM Row - only show if we have time entries */}
        {accruedTimes.length > 0 && (
          <div className={metricsRowClassName}>
            {/* Only add spacer if we're showing price quoted */}
            {plannedFee && <div className={gpmRowSpacerClassName} />}

            {(showGpm || (showGpmSlider && gpmDangerThreshold && gpmWarningThreshold)) && (
              <MetricCard
                label={showGpm ? "Gross profit margin" : "Gross profit margin"}
                value={showGpm ? `${Math.round(getGpmValue(actualGpm))}%` : ""}
                tone={
                  showGpm && gpmDangerThreshold
                    ? getGpmValue(actualGpm) < gpmDangerThreshold
                      ? Tones.Error
                      : Tones.Success
                    : Tones.Normal
                }
                secondary={
                  showGpm && gpmDangerThreshold !== undefined
                    ? `${Math.round(Math.abs(getGpmValue(actualGpm) - gpmDangerThreshold))}% ${
                        getGpmValue(actualGpm) < gpmDangerThreshold ? "below" : "above"
                      } the firm's requirement for ${gpmDangerThreshold}%`
                    : undefined
                }
                slider={
                  showGpmSlider && gpmDangerThreshold && gpmWarningThreshold ? (
                    <GpmSlider
                      showGpm={showGpm}
                      gpm={actualGpm}
                      gpmThreshold={{ gpmDangerThreshold, gpmWarningThreshold }}
                      isFeeEarnerLevelGpm={false}
                      timeEntryCount={accruedTimes.length}
                    />
                  ) : undefined
                }
              />
            )}

            {/* Only show GPM after markup/write-off if we have plannedFee */}
            {plannedFee &&
              (showGpm || (showGpmSlider && gpmDangerThreshold && gpmWarningThreshold)) &&
              gpmAfterWriteOff && (
                <MetricCard
                  label={showGpm ? "GPM after markup/write-off" : "GPM after markup/write-off"}
                  value={showGpm ? `${Math.round(getGpmValue(gpmAfterWriteOff))}%` : ""}
                  tone={
                    showGpm && gpmDangerThreshold
                      ? getGpmValue(gpmAfterWriteOff) < gpmDangerThreshold
                        ? Tones.Error
                        : Tones.Success
                      : Tones.Normal
                  }
                  secondary={
                    showGpm && gpmDangerThreshold !== undefined
                      ? `${Math.round(
                          Math.abs(getGpmValue(gpmAfterWriteOff) - gpmDangerThreshold)
                        )}% ${
                          getGpmValue(gpmAfterWriteOff) < gpmDangerThreshold ? "below" : "above"
                        } the firm's requirement for ${gpmDangerThreshold}%`
                      : undefined
                  }
                  slider={
                    showGpmSlider && gpmDangerThreshold && gpmWarningThreshold ? (
                      <GpmSlider
                        showGpm={showGpm}
                        gpm={gpmAfterWriteOff}
                        gpmThreshold={{ gpmDangerThreshold, gpmWarningThreshold }}
                        isFeeEarnerLevelGpm={false}
                        timeEntryCount={accruedTimes.length}
                      />
                    ) : undefined
                  }
                />
              )}

            {/* Spacer or GPM based on billed amount */}
            {showGpm || (showGpmSlider && gpmDangerThreshold && gpmWarningThreshold) ? (
              amountBilled > 0 && gpmRecovered ? (
                <MetricCard
                  label={showGpm ? "GPM based on billed amount" : "GPM based on billed amount"}
                  value={showGpm ? `${Math.round(getGpmValue(gpmRecovered))}%` : ""}
                  tone={
                    showGpm && gpmDangerThreshold
                      ? getGpmValue(gpmRecovered) < gpmDangerThreshold
                        ? Tones.Error
                        : Tones.Success
                      : Tones.Normal
                  }
                  secondary={
                    showGpm && gpmDangerThreshold !== undefined
                      ? `${Math.round(Math.abs(getGpmValue(gpmRecovered) - gpmDangerThreshold))}% ${
                          getGpmValue(gpmRecovered) < gpmDangerThreshold ? "below" : "above"
                        } the firm's requirement for ${gpmDangerThreshold}%`
                      : undefined
                  }
                  slider={
                    showGpmSlider && gpmDangerThreshold && gpmWarningThreshold ? (
                      <GpmSlider
                        showGpm={showGpm}
                        gpm={gpmRecovered}
                        gpmThreshold={{ gpmDangerThreshold, gpmWarningThreshold }}
                        isFeeEarnerLevelGpm={false}
                        timeEntryCount={accruedTimes.length}
                      />
                    ) : undefined
                  }
                />
              ) : (
                <div className={gpmRowSpacerClassName} />
              )
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const toneToClassName: Record<Tones, string> = {
  [Tones.Normal]: normalClassName,
  [Tones.Error]: errorClassName,
  [Tones.Success]: successClassName,
};

type MetricCardProps = {
  label: string;
  value: string;
  tone?: Tones | { lower: Tones; upper: Tones };
  secondary?: string;
  expandable?: React.ReactNode;
  slider?: React.ReactNode;
};

const MetricCard: FC<MetricCardProps> = ({
  label,
  value,
  tone = Tones.Normal,
  secondary,
  expandable,
  slider,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const content = (
    <>
      <span className={labelClassName}>{label}</span>
      <div
        className={`${valueClassName} ${
          typeof tone === "object"
            ? `${toneToClassName[tone.lower]} ${toneToClassName[tone.upper]}`
            : toneToClassName[tone]
        }`}
      >
        {value}
      </div>
      {secondary && <div className={secondaryTextClassName}>{secondary}</div>}
      {slider && <div className={sliderContainerClassName}>{slider}</div>}
    </>
  );

  if (expandable) {
    return (
      <>
        <div
          className={`${metricCardClassName} ${metricCardClickableClassName}`}
          onClick={() => setIsModalVisible(true)}
        >
          {content}
        </div>
        <Modal
          title={label}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={800}
        >
          <div className={modalTableClassName}>{expandable}</div>
        </Modal>
      </>
    );
  }

  return <div className={metricCardClassName}>{content}</div>;
};
